import React, { useCallback, useEffect, useMemo } from 'react';
import { PageHeaderPrimitives } from '@uhg-abyss/web/ui/PageHeader';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { TopNavMenu } from '../TopNavMenu';
import { Label } from '@uhg-abyss/web/ui/Label';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { styled } from '@uhg-abyss/web/tools/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { StateRouter } from '@uhg-abyss/web/ui/StateRouter';
import {
  getCommunicationsCornerContent,
  getResourcesContent,
} from '../DropdownMenuHelpers';
import { saveAs } from 'file-saver';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Grid } from '@uhg-abyss/web/ui/Grid';

const HeaderChildItemsDisplay = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
});

// Extract submenu item component to reduce nesting
const SubmenuItem = React.memo(({ item, index, fireHeaderEvent }) => {
  return (
    <Grid.Col
      key={item?.id || `submenu-${index}`}
      span={4}
      css={{
        padding: '24px 32px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <h4
        style={{
          fontWeight: '700',
          fontSize: '16px',
          fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
          padding: '8px 0px',
        }}
      >
        {item?.menuTitle}
      </h4>
      <Layout.Stack alignItems="left" space="0">
        {Array.isArray(item?.paths) &&
          item.paths.map((path, pathIndex) => {
            return (
              <NavMenuPrimitives.Link
                key={path?.id || `path-sub-${index}-${pathIndex}`}
                css={{
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#196ECF',
                  height: '40px',
                  paddingLeft: '0px !important',
                  textTransform: 'capitalize',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
                variant="custom"
                href={path?.path}
                onClick={() => {
                  fireHeaderEvent(path?.name, item?.menuTitle);
                }}
              >
                {path?.name}
              </NavMenuPrimitives.Link>
            );
          })}
      </Layout.Stack>
    </Grid.Col>
  );
});

// Extract main paths column component
const MainPathsColumn = React.memo(
  ({ paths, menuTitle, fireHeaderEvent, subMenuTitle }) => {
    return (
      <Grid.Col
        span={3}
        css={{
          paddingLeft: '48px !important',
          borderRight:
            Array.isArray(paths) && paths.length > 0
              ? '1px solid #e0e0e0'
              : 'none',
          width: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <h3
          style={{
            fontWeight: '700',
            fontSize: '18px',
            fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
            padding: '8px 0px',
          }}
        >
          {menuTitle}
        </h3>
        <Layout.Stack alignItems="left" space="0">
          {Array.isArray(paths) &&
            paths.map((path, index) => {
              return (
                <NavMenuPrimitives.Link
                  key={path.id || `path-main-${index}`}
                  css={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#196ECF',
                    height: '40px',
                    paddingLeft: '0px !important',
                    textTransform: 'capitalize',
                  }}
                  variant="custom"
                  href={path.path}
                  onClick={() => {
                    fireHeaderEvent(path.name, subMenuTitle);
                  }}
                >
                  {path.name}
                </NavMenuPrimitives.Link>
              );
            })}
        </Layout.Stack>
      </Grid.Col>
    );
  }
);

// Extract submenu column component
const SubmenuColumn = React.memo(({ sortedSubmenu, fireHeaderEvent }) => {
  if (!Array.isArray(sortedSubmenu) || sortedSubmenu.length === 0) {
    return null;
  }

  return (
    <Grid.Col
      span={9}
      css={{
        boxSizing: 'border-box',
        overflow: 'hidden',
        padding: 0,
      }}
    >
      <Grid
        css={{
          height: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        {sortedSubmenu.map((item, index) => {
          return (
            <SubmenuItem
              key={item?.id || `submenu-item-${index}`}
              item={item}
              index={index}
              fireHeaderEvent={fireHeaderEvent}
            />
          );
        })}
      </Grid>
    </Grid.Col>
  );
});

export const DesktopHeader = ({
  signIn,
  signOut,
  fireHeaderEvent,
  routeToPage,
  setShowSubscribeAPIModal,
  isLoggedIn,
  filteredLeftNavData,
  getPathsByTagInfo,
  subMenuTitle,
  subMenuInfo,
}) => {
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });

  const HEADER_LINK_CLICK = 'header link click';

  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.id = 'nav-menu-viewport-fix';
    styleTag.innerHTML = `
      div[data-abyss-portal="true"].abyss-nav-menu-viewport-container {
        left: auto !important;
        width: 1248px !important;
      }
    `;

    // Add to head
    document.head.appendChild(styleTag);

    const styleTag2 = document.createElement('style');
    styleTag2.id = 'nav-menu-scroll-button-fix';
    styleTag2.innerHTML = `
      .abyss-nav-menu-scroll-button-left {
        display: none !important;
      }
    `;

    // Add to head
    document.head.appendChild(styleTag2);

    // Clean up
    return () => {
      const existingTag = document.getElementById('nav-menu-viewport-fix');
      if (existingTag) existingTag.remove();
      const existingTag2 = document.getElementById(
        'nav-menu-scroll-button-fix'
      );
      if (existingTag2) existingTag2.remove();
    };
  }, []);

  const getIntroductionMenu = (pathsByTag) => {
    const filterPathsByTag = (paths) => {
      return paths?.filter((el) => {
        return (
          el.tag === 'Overview & Onboarding' || el.tag === 'Getting Started'
        );
      });
    };

    const createMenuItem = (id, path) => {
      const handleClick = () => {
        if (path?.id.includes('download')) {
          saveAs(path.path);
        } else {
          fireHeaderEvent(path?.name, 'overview & onboarding');
        }
      };

      return (
        <NavMenuPrimitives.Link
          href={`${path?.path}`}
          key={`intro${path?.name}`}
          title={path?.name}
          onClick={handleClick}
          css={{ color: '#196ECF', fontWeight: 'bold', fontSize: '15px' }}
        >
          {path?.name}
        </NavMenuPrimitives.Link>
      );
    };

    const mapPathsToMenuItems = (id, subPaths) => {
      return subPaths.map((path) => {
        return createMenuItem(id, path);
      });
    };

    const createMenuItems = (paths) => {
      return paths?.flatMap((el) => {
        return mapPathsToMenuItems(el.id, el.paths);
      });
    };

    const filteredPaths = filterPathsByTag(pathsByTag);
    return <React.Fragment>{createMenuItems(filteredPaths)}</React.Fragment>;
  };

  const KbDropdownMenuContent = () => {
    let pathsByTag = filteredLeftNavData();

    return (
      <React.Fragment>
        <NavMenuPrimitives.Columns
          css={{
            'abyss-nav-menu-column': {
              zIndex: 150,
              backgroundColor: '#fff',
              padding: '0px 0px 0px 0px !important',
            },
            '.abyss-nav-menu-column-root:first-child': {
              width: '18%',
            },
            '.abyss-nav-menu-column-root:nth-child(2)': {
              width: '82%',
            },
            'abyss-nav-menu-column-title': {
              fontSize: '16px !important',
              fontWeight: '700 !important',
            },
          }}
        >
          <NavMenuPrimitives.Column title="Overview & Onboarding">
            {getIntroductionMenu(pathsByTag)}
          </NavMenuPrimitives.Column>
          <NavMenuPrimitives.Column columnCount={2} title="API Library">
            {getPathsByTagInfo(pathsByTag)}
          </NavMenuPrimitives.Column>
        </NavMenuPrimitives.Columns>
      </React.Fragment>
    );
  };

  const priorities = {
    overview: 1,
    'technical demo videos': 2,
    'frequently asked questions': 3,
    'technical documentation': 4,
    // Default priority for other paths
    default: 100,
  };

  // Memoize the getPathPriority function
  const getPathPriority = useCallback(
    (pathName) => {
      if (!pathName) return priorities.default;

      const pathNameLower =
        typeof pathName === 'string' ? pathName.toLowerCase() : '';

      // Find matching priority or use default
      const matchingKey = Object.keys(priorities).find((key) => {
        return pathNameLower.includes(key);
      });
      return matchingKey ? priorities[matchingKey] : priorities.default;
    },
    [priorities]
  );

  // Memoize the sort function to prevent recreation
  const sortSubmenu = useCallback(
    (submenuArray) => {
      if (!Array.isArray(submenuArray)) return [];

      return [...submenuArray].sort((a, b) => {
        // Safely get lowercase titles or empty strings if undefined
        const titleA =
          typeof a?.menuTitle === 'string' ? a.menuTitle.toLowerCase() : '';
        const titleB =
          typeof b?.menuTitle === 'string' ? b.menuTitle.toLowerCase() : '';

        // Find matching priority key for items
        const priorityA = getPathPriority(titleA);
        const priorityB = getPathPriority(titleB);

        // If priorities are the same, sort alphabetically
        if (priorityA === priorityB) {
          return titleA.localeCompare(titleB);
        }

        // Sort by priority (ascending)
        return priorityA - priorityB;
      });
    },
    [getPathPriority]
  );

  // Now refactor the main function with reduced nesting
  const getKnowledgebaseSubMenu = useCallback(
    (fireHeaderEvent, menuTitle, subMenu) => {
      // Extract paths and prepare submenu
      const { paths } = subMenu || {};

      // Memoize the sorted submenu
      const sortedSubmenu = useMemo(() => {
        let result = [];

        if (subMenu?.hasSubmenu && Array.isArray(subMenu.submenu)) {
          result = sortSubmenu(subMenu.submenu);
        }

        return result;
      }, [subMenu, sortSubmenu]);

      // Memoize the rendered component
      return useMemo(() => {
        return (
          <div css={{ position: 'relative' }}>
            <Grid
              css={{
                height: 'auto',
                width: '100%',
                maxWidth: '100%',
                overflow: 'visible',
                position: 'relative',
              }}
            >
              <MainPathsColumn
                paths={paths}
                menuTitle={menuTitle}
                fireHeaderEvent={fireHeaderEvent}
                subMenuTitle={subMenuTitle}
              />
              <SubmenuColumn
                sortedSubmenu={sortedSubmenu}
                fireHeaderEvent={fireHeaderEvent}
              />
            </Grid>
          </div>
        );
      }, [sortedSubmenu, paths, menuTitle, fireHeaderEvent, subMenuTitle]);
    },
    [sortSubmenu]
  );

  const getKnowledgebaseDropdown = () => {
    return (
      <React.Fragment>
        <NavMenuPrimitives.Trigger css={{ fontSize: '15px', fontWeight: 600 }}>
          API Library
        </NavMenuPrimitives.Trigger>
        <NavMenuPrimitives.Content>
          <StateRouter.Provider initialPath="/knowledgebase">
            <NavMenuPrimitives.RouterHeader />
            <StateRouter.Routes>
              <StateRouter.Route path="/knowledgebase" title="Knowledge Base">
                <KbDropdownMenuContent />
              </StateRouter.Route>
              <StateRouter.Route
                path="/knowledgebase/submenu"
                title={subMenuTitle}
              >
                {getKnowledgebaseSubMenu(
                  fireHeaderEvent,
                  subMenuTitle,
                  subMenuInfo
                )}
              </StateRouter.Route>
            </StateRouter.Routes>
          </StateRouter.Provider>
        </NavMenuPrimitives.Content>
      </React.Fragment>
    );
  };

  const getMenuItems = () => {
    const bottomMenuHeaderItems = [
      {
        title: 'Home',
        href: '/',
        header: 'notSignedIn',
        onClick: () => {
          return fireHeaderEvent('Home', 'header', HEADER_LINK_CLICK);
        },
      },
      {
        title: 'Getting Started',
        href: '/getting-started',
        header: 'notSignedIn',
        onClick: () => {
          return fireHeaderEvent(
            'Getting Started',
            'header',
            HEADER_LINK_CLICK
          );
        },
      },
      {
        title: 'Dashboard',
        href: '/welcome',
        header: 'signedIn',
        onClick: () => {
          return fireHeaderEvent('Dashboard', 'header', HEADER_LINK_CLICK);
        },
      },
      {
        title: 'API Library',
        header: 'both',
        content: getKnowledgebaseDropdown(),
      },
      {
        title: 'Resources',
        header: 'signedIn',
        content: getResourcesContent(fireHeaderEvent, routeToPage, dsUserInfo),
      },
    ];
    const allCommunication = {
      title: 'Communications Corner',
      header: 'signedIn',
      content: getCommunicationsCornerContent(
        fireHeaderEvent,
        routeToPage,
        setShowSubscribeAPIModal,
        dsUserInfo
      ),
    };

    const bottomMenuItemsList = [...bottomMenuHeaderItems, allCommunication];
    const headerMenuItems = (isUserAuthorized) => {
      return bottomMenuItemsList?.filter((el) => {
        return el.header === isUserAuthorized || el.header === 'both';
      });
    };

    const unAuthorizedUserHeaderMenu = headerMenuItems('notSignedIn');
    const autorizedUserHeaderMenu = headerMenuItems('signedIn');

    const bottomMenuItems = isLoggedIn
      ? autorizedUserHeaderMenu
      : unAuthorizedUserHeaderMenu;

    const renderLink = (item) => {
      return (
        <NavMenuPrimitives.Link
          key={item.title}
          href={item.href}
          onClick={item.onClick}
          css={{ fontSize: '15px', fontWeight: 600 }}
        >
          {item.title}
        </NavMenuPrimitives.Link>
      );
    };

    const renderDropdown = (item) => {
      return (
        <NavMenuPrimitives.Item key={item.title}>
          {item.content}
        </NavMenuPrimitives.Item>
      );
    };

    return (
      <NavMenuPrimitives.List>
        {bottomMenuItems.map((item) => {
          return item.content ? renderDropdown(item) : renderLink(item);
        })}
      </NavMenuPrimitives.List>
    );
  };

  return (
    <React.Fragment>
      <PageHeaderPrimitives.Container
        css={{
          'abyss-page-header-root': {
            zIndex: 150,
            position: 'relative',
          },
          '.abyss-nav-menu-scroll-button-right': {
            display: 'none !important',
          },
          '.abyss-nav-menu-scroll-button-left': {
            display: 'none !important',
          },
        }}
      >
        <PageHeaderPrimitives.Brandmark
          logoRedirect="/"
          logoTitle="API Marketplace"
          css={{
            'abyss-page-header-logo': {
              flexGrow: '0 !important',
            },
            'abyss-page-header-logo-title': {
              color: '#002677',
              fontSize: '18px',
            },
            '.abyss-page-header-toolbars': {
              minWidth: '500px',
            },
          }}
        />

        <div>
          <div style={{ paddingBottom: '16px' }}>
            <TopNavMenu
              userInfo={userInfo}
              signOut={signOut}
              signIn={signIn}
              fireHeaderEvent={fireHeaderEvent}
              routeToPage={routeToPage}
            />
          </div>
          {dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE' && (
            <HeaderChildItemsDisplay>
              <Label color="$gray8">My Organization:&nbsp;</Label>
              <Badge variant="info">{dsUserInfo?.orgName}</Badge>
            </HeaderChildItemsDisplay>
          )}
        </div>
      </PageHeaderPrimitives.Container>
      <NavMenuPrimitives.Provider seoMode>
        <NavMenuPrimitives.Root
          zIndex={150}
          enableOutsideScroll
          hasOverlay
          css={{
            'abyss-nav-menu-container': {
              maxWidth: '1248px',
            },
            '.abyss-nav-menu-scroll-button-left': {
              display: 'none !important',
            },
          }}
        >
          {getMenuItems()}
        </NavMenuPrimitives.Root>
      </NavMenuPrimitives.Provider>
    </React.Fragment>
  );
};

DesktopHeader.propTypes = {
  signIn: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  fireHeaderEvent: PropTypes.func.isRequired,
  routeToPage: PropTypes.func.isRequired,
  setShowSubscribeAPIModal: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  filteredLeftNavData: PropTypes.func.isRequired,
  getPathsByTagInfo: PropTypes.func.isRequired,
  subMenuTitle: PropTypes.string.isRequired,
  subMenuInfo: PropTypes.object.isRequired,
};
